import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Ear, Gauge } from 'lucide-react';
import { Section, Eyebrow, H2, Reveal, Ref, FONT, MONO } from './ui';

export const Practice = ({ d, L, t }) => (
  <>
    <Section id="method" dark>
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
        <Reveal>
          <Eyebrow dark>{L(d.method.eyebrow)}</Eyebrow>
          <H2 dark className="mt-4">{L(d.method.title)}</H2>
          <p className="mt-6 text-lg text-blue-100/80">{L(d.method.body)}</p>
        </Reveal>
        <Reveal delay={150}>
          <div className="grid grid-cols-2 gap-4" data-testid="vocal-fitness-vowel-count-chart">
            {d.method.stats.map((s, i) => (
              <div key={i} className={`rounded-3xl p-6 border ${i === 1 ? 'bg-blue-600 border-blue-400' : 'bg-white/[0.04] border-white/10'}`}>
                <p className="text-6xl lg:text-7xl font-extrabold tracking-tight" style={FONT}>{s.n}</p>
                <p className="mt-1 text-sm text-blue-100/80">{L(s.label)}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </Section>
    <Section id="lab">
      <div className="grid lg:grid-cols-2 gap-12">
        <Reveal>
          <Eyebrow>{L(d.lab.eyebrow)}</Eyebrow>
          <H2 className="mt-4">{L(d.lab.title)}</H2>
          <p className="mt-6 text-slate-600">{L(d.lab.body)}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/lms/phonemes" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold shadow-lg shadow-blue-500/25 hover:-translate-y-0.5 transition-transform" data-testid="cta-phonetic-lab">{t.openLab} <ArrowUpRight className="w-4 h-4" /></Link>
            <Link to="/lms/phoneme/u-foot" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-300 font-semibold hover:border-blue-500 transition-colors" data-testid="cta-foot-card">/ʊ/ FOOT</Link>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-7" data-testid="lab-inside">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold" style={MONO}>{t.inside}</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-2">
              {L(d.lab.inside).map((x) => <p key={x} className="rounded-xl bg-white border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">{x}</p>)}
            </div>
          </div>
        </Reveal>
      </div>
      <div className="mt-16 grid lg:grid-cols-[1fr_1.4fr] gap-8">
        <Reveal>
          <div className="rounded-3xl bg-amber-50 border border-amber-200 p-8 h-full" data-testid="ear-training-quiz">
            <Ear className="w-8 h-8 text-amber-600" />
            <p className="mt-3 text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold" style={MONO}>{L(d.ear.eyebrow)}</p>
            <p className="mt-2 text-2xl font-extrabold" style={FONT}>{L(d.ear.title)}</p>
            <div className="mt-4 flex gap-2">{['A', 'B', 'C'].map((x) => <span key={x} className="w-12 h-12 rounded-xl bg-white border-2 border-amber-300 flex items-center justify-center text-xl font-extrabold">{x}</span>)}</div>
            <p className="mt-4 text-slate-700 text-sm">{L(d.ear.body)}</p>
            <p className="mt-3 text-sm font-bold text-amber-800">{d.ear.pairs.join('  ·  ')}</p>
            <div className="mt-4"><Ref>{d.ear.ref}</Ref></div>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="rounded-3xl bg-[#060d24] text-white p-8 h-full" data-testid="level-test-preview">
            <div className="flex items-center gap-3"><Gauge className="w-7 h-7 text-cyan-300" /><p className="text-xs uppercase tracking-[0.2em] text-blue-200" style={MONO}>{L(d.levelTest.eyebrow)}</p></div>
            <p className="mt-3 text-2xl font-extrabold" style={FONT}>{L(d.levelTest.title)}</p>
            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {d.levelTest.items.map((x) => (
                <div key={x.word} className="rounded-2xl bg-white/[0.05] border border-white/10 p-4">
                  <p className="text-3xl font-extrabold" style={FONT}>{x.word}</p>
                  <p className="text-sm text-cyan-300 mt-1" style={MONO}>{x.sym}</p>
                  <p className="text-xs text-blue-100/70 mt-2">{L(x.body)}</p>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm text-blue-100/80">{L(d.levelTest.how)}</p>
            <p className="mt-2 text-xs text-blue-200/50">{L(d.levelTest.disclaimer)}</p>
          </div>
        </Reveal>
      </div>
    </Section>
    <Section id="exercises" className="bg-gradient-to-b from-blue-50 to-white">
      <Reveal><Eyebrow>{L(d.exercises.eyebrow)}</Eyebrow><H2 className="mt-4">{L(d.exercises.title)}</H2></Reveal>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="lab-exercises">
        {d.exercises.items.map((x, i) => (
          <Reveal key={x.n} delay={i * 100}>
            <div className="h-full rounded-3xl bg-white border border-slate-200 p-7 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-500/10 transition-[transform,box-shadow] duration-300">
              <span className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center text-xl font-extrabold" style={FONT}>{x.n}</span>
              <p className="mt-5 text-xl font-extrabold" style={FONT}>{L(x.title)}</p>
              <p className="mt-2 text-sm text-slate-600">{L(x.body)}</p>
              {x.ipa && <p className="mt-3 text-sm text-blue-700" style={MONO}>{x.ipa}</p>}
            </div>
          </Reveal>
        ))}
      </div>
    </Section>
  </>
);
